import * as store from './data/store';
import { start as startSimulator } from './data/simulator';

store.seed();
startSimulator();

export interface Vehicle {
  id: string;
  name: string;
  type: string;
  home_base: string;
  current_driver: string;
  status: string;
  latitude: number;
  longitude: number;
  heading: number;
  speed: number;
  total_km: number;
  uptime_hours: number;
}

export interface Shipment {
  id: string;
  vehicle_id: string;
  origin: string;
  destination: string;
  cargo_type: string;
  cargo_description: string;
  temp_min: number;
  temp_max: number;
  humidity_min: number | null;
  humidity_max: number | null;
  pickup_time: string;
  eta: string;
  actual_delivery: string | null;
  status: string;
  current_temp: number | null;
  current_humidity: number | null;
  vehicle_name?: string;
}

export interface TelemetryReading {
  id: number;
  vehicle_id: string;
  shipment_id: string;
  timestamp: string;
  latitude: number;
  longitude: number;
  temperature: number;
  humidity: number;
  door_open: boolean;
  speed: number;
}

export interface Alert {
  id: number;
  vehicle_id: string;
  shipment_id: string;
  event_type: string;
  severity: string;
  description: string;
  resolved: boolean;
  resolved_at: string | null;
  created_at: string;
  vehicle_name?: string;
  shipment_destination?: string;
}

export interface RoutePoint {
  lat: number;
  lng: number;
}

export interface VehicleRoute {
  waypoints: RoutePoint[];
}

export interface Stats {
  total_vehicles: number;
  active_vehicles: number;
  in_transit: number;
  active_alerts: number;
  delivered_today: number;
  avg_temp: number;
}

function downloadCSV(content: string, filename: string) {
  const blob = new Blob([content], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function esc(val: string): string {
  if (val.includes(',') || val.includes('"') || val.includes('\n')) return `"${val.replace(/"/g, '""')}"`;
  return val;
}

function csvRows(rows: string[][]): string {
  return rows.map(r => r.map(esc).join(',')).join('\n');
}

export function exportVehiclesCSV() {
  const vs = store.getVehicles();
  downloadCSV(csvRows([
    ['ID','Name','Type','Home Base','Driver','Status','Latitude','Longitude','Speed (km/h)','Total KM','Uptime (hours)'],
    ...vs.map(v => [v.id,v.name,v.type,v.home_base,v.current_driver,v.status,
      v.latitude.toFixed(6),v.longitude.toFixed(6),v.speed.toFixed(1),v.total_km.toFixed(1),v.uptime_hours.toFixed(1)]),
  ]), 'vehicles.csv');
}

export function exportShipmentsCSV(status?: string) {
  const ss = store.getShipments(status);
  downloadCSV(csvRows([
    ['ID','Vehicle ID','Vehicle','Origin','Destination','Cargo Type','Cargo Description','Status',
     'Temp Min (°C)','Temp Max (°C)','Current Temp (°C)','Humidity Min (%)','Humidity Max (%)',
     'Current Humidity (%)','Pickup Time','ETA','Actual Delivery'],
    ...ss.map(s => [s.id,s.vehicle_id,s.vehicle_name||'',s.origin,s.destination,s.cargo_type,
      s.cargo_description,s.status,s.temp_min.toFixed(1),s.temp_max.toFixed(1),
      s.current_temp!=null?s.current_temp.toFixed(1):'',
      s.humidity_min!=null?s.humidity_min.toFixed(1):'',s.humidity_max!=null?s.humidity_max.toFixed(1):'',
      s.current_humidity!=null?s.current_humidity.toFixed(1):'',s.pickup_time,s.eta,s.actual_delivery||'']),
  ]), 'shipments.csv');
}

export function exportTelemetryCSV(shipmentId: string) {
  const ts = store.getShipmentTelemetry(shipmentId);
  downloadCSV(csvRows([
    ['Timestamp','Vehicle ID','Shipment ID','Latitude','Longitude','Temperature (°C)','Humidity (%)','Door Open','Speed (km/h)'],
    ...ts.map(t => [t.timestamp,t.vehicle_id,t.shipment_id,t.latitude.toFixed(6),t.longitude.toFixed(6),
      t.temperature.toFixed(2),t.humidity.toFixed(1),String(t.door_open),t.speed.toFixed(1)]),
  ]), `telemetry-${shipmentId}.csv`);
}

export const api = {
  getVehicles: () => Promise.resolve(store.getVehicles()),
  getVehicle: (id: string) => {
    const v = store.getVehicle(id);
    return v ? Promise.resolve(v) : Promise.reject(new Error('vehicle not found'));
  },
  getVehicleShipments: (id: string) => Promise.resolve(store.getVehicleShipments(id)),
  getVehicleAlerts: (id: string) => Promise.resolve(store.getVehicleAlerts(id)),
  getVehicleRoute: (id: string) => Promise.resolve(store.getVehicleRoute(id)),
  getShipments: (status?: string) => Promise.resolve(store.getShipments(status)),
  getShipment: (id: string) => {
    const s = store.getShipment(id);
    return s ? Promise.resolve(s) : Promise.reject(new Error('shipment not found'));
  },
  getShipmentTelemetry: (id: string, from?: string, to?: string) =>
    Promise.resolve(store.getShipmentTelemetry(id, from, to)),
  getAlerts: (resolved?: string) => Promise.resolve(store.getAlerts(resolved)),
  resolveAlert: (id: number) => { store.resolveAlert(id); return Promise.resolve({ status: 'ok' as const }); },
  getStats: () => Promise.resolve(store.getStats()),
  createVehicle: (v: Partial<Vehicle>) => {
    try { return Promise.resolve(store.createVehicle(v)); }
    catch (e) { return Promise.reject(e); }
  },
  createShipment: (s: Partial<Shipment>) => {
    try { return Promise.resolve(store.createShipment(s)); }
    catch (e) { return Promise.reject(e); }
  },
};
